import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  Calendar, 
  Clock, 
  MessageSquare,
  CheckCircle2,
  ArrowRight,
  Flame,
  Droplets,
  Zap,
  Wrench,
  Stethoscope,
  AlertTriangle,
  DollarSign,
  Users,
  Bell,
  BarChart3,
  Mail,
  MapPin,
  Menu,
  X,
  Shield,
  Play,
  ChevronDown,
  Mic,
  MessageCircle,
  Home,
  TreePine,
  Car,
  UtensilsCrossed,
  Fish,
  Mountain,
  Dumbbell,
  Building2,
  Heart
} from "lucide-react";
import { Button } from "@/components/ui/button";
import ChatBot from "@/components/ChatBot";
import LeadMagnet from "@/components/LeadMagnet";
import FeaturedContractors from "@/components/FeaturedContractors";
import AnimatedAuroraBackground from "@/components/AnimatedAuroraBackground";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// State abbreviations for dropdown
const statesList = [
  { name: "Alabama", abbr: "AL" }, { name: "Alaska", abbr: "AK" }, { name: "Arizona", abbr: "AZ" },
  { name: "Arkansas", abbr: "AR" }, { name: "California", abbr: "CA" }, { name: "Colorado", abbr: "CO" },
  { name: "Connecticut", abbr: "CT" }, { name: "Delaware", abbr: "DE" }, { name: "Florida", abbr: "FL" },
  { name: "Georgia", abbr: "GA" }, { name: "Hawaii", abbr: "HI" }, { name: "Idaho", abbr: "ID" },
  { name: "Illinois", abbr: "IL" }, { name: "Indiana", abbr: "IN" }, { name: "Iowa", abbr: "IA" },
  { name: "Kansas", abbr: "KS" }, { name: "Kentucky", abbr: "KY" }, { name: "Louisiana", abbr: "LA" },
  { name: "Maine", abbr: "ME" }, { name: "Maryland", abbr: "MD" }, { name: "Massachusetts", abbr: "MA" },
  { name: "Michigan", abbr: "MI" }, { name: "Minnesota", abbr: "MN" }, { name: "Mississippi", abbr: "MS" },
  { name: "Missouri", abbr: "MO" }, { name: "Montana", abbr: "MT" }, { name: "Nebraska", abbr: "NE" },
  { name: "Nevada", abbr: "NV" }, { name: "New Hampshire", abbr: "NH" }, { name: "New Jersey", abbr: "NJ" },
  { name: "New Mexico", abbr: "NM" }, { name: "New York", abbr: "NY" }, { name: "North Carolina", abbr: "NC" },
  { name: "North Dakota", abbr: "ND" }, { name: "Ohio", abbr: "OH" }, { name: "Oklahoma", abbr: "OK" },
  { name: "Oregon", abbr: "OR" }, { name: "Pennsylvania", abbr: "PA" }, { name: "Rhode Island", abbr: "RI" },
  { name: "South Carolina", abbr: "SC" }, { name: "South Dakota", abbr: "SD" }, { name: "Tennessee", abbr: "TN" },
  { name: "Texas", abbr: "TX" }, { name: "Utah", abbr: "UT" }, { name: "Vermont", abbr: "VT" },
  { name: "Virginia", abbr: "VA" }, { name: "Washington", abbr: "WA" }, { name: "West Virginia", abbr: "WV" },
  { name: "Wisconsin", abbr: "WI" }, { name: "Wyoming", abbr: "WY" }
];

// Industries data with slugs for routing
const industries = [
  { icon: Flame, name: "HVAC & Heating", slug: "hvac" },
  { icon: Droplets, name: "Plumbing", slug: "plumbing" },
  { icon: Zap, name: "Electrical", slug: "electrical" },
  { icon: Wrench, name: "Home Repair & Remodeling", slug: "home_repair" },
  { icon: Stethoscope, name: "Dental & Medical Offices", slug: "dental" },
  { icon: Home, name: "Roofing", slug: "roofing" },
  { icon: TreePine, name: "Landscaping", slug: "landscaping" },
  { icon: Car, name: "Auto Repair", slug: "auto_repair" },
  { icon: Droplets, name: "Septic Services", slug: "septic" },
  { icon: UtensilsCrossed, name: "Restaurant", slug: "restaurant" },
  { icon: Fish, name: "Fishing Charter", slug: "fishing_charter" },
  { icon: Mountain, name: "Sightseeing Tours", slug: "sightseeing" },
  { icon: Dumbbell, name: "Gym & Fitness", slug: "gym" },
  { icon: Building2, name: "Real Estate", slug: "real_estate" },
  { icon: Heart, name: "Health & Wellness Spa", slug: "wellness_spa" }
];

// How it works steps
const howItWorks = [
  {
    id: "step-1",
    step: "1",
    title: "Customer texts or calls",
    description: "Our AI responds in under 60 seconds"
  },
  {
    id: "step-2",
    step: "2", 
    title: "Qualifies the urgency",
    description: "Emergency vs. routine, exactly like you would"
  },
  {
    id: "step-3",
    step: "3",
    title: "Books the appointment",
    description: "Straight into your calendar with confirmation sent"
  },
  {
    id: "step-4",
    step: "4",
    title: "You get alerted",
    description: "Know exactly what's coming and when"
  }
];

// Benefits
const benefits = [
  { id: "benefit-1", text: "24/7 lead response (no voicemail, ever)" },
  { id: "benefit-2", text: "Automatic appointment booking" },
  { id: "benefit-3", text: "After-hours inquiry handling" },
  { id: "benefit-4", text: "Weekly reports showing leads captured" },
  { id: "benefit-5", text: "Works with your existing phone/calendar" },
  { id: "benefit-6", text: "No app to learn, no staff to train" }
];

// Problem stats
const problemStats = [
  { id: "stat-1", stat: "62%", label: "of inbound calls go unanswered during busy hours" },
  { id: "stat-2", stat: "$1,500+", label: "average revenue lost per missed emergency call" },
  { id: "stat-3", stat: "$4,000+/mo", label: "cost to hire a full-time dispatcher" }
];

// Testimonials - Anonymous/representative examples
const testimonials = [
  {
    id: "testimonial-1",
    quote: "We were losing 15-20 calls a week during the January cold snap. Now every lead gets a response immediately. We've booked 18 extra jobs in the first month.",
    highlight: "18 extra jobs",
    author: "HVAC Contractor",
    role: "Business Owner",
    company: "Anchorage Area",
    industry: "Heating & Cooling",
    initials: "HC",
    gradient: "from-orange-400 to-red-500"
  },
  {
    id: "testimonial-2",
    quote: "I was skeptical about AI answering my phones, but customers can't tell the difference. It's like having a full-time receptionist for a fraction of the cost.",
    highlight: "fraction of the cost",
    author: "Plumbing Company",
    role: "Owner-Operator",
    company: "Mat-Su Valley",
    industry: "Plumbing Services",
    initials: "PC",
    gradient: "from-blue-400 to-cyan-500"
  },
  {
    id: "testimonial-3",
    quote: "The after-hours coverage alone paid for itself. We captured 12 emergency calls last month that would have gone to competitors.",
    highlight: "12 emergency calls",
    author: "Electrical Contractor",
    role: "Operations Manager",
    company: "Kenai Peninsula",
    industry: "Electrical Services",
    initials: "EC",
    gradient: "from-yellow-400 to-amber-500"
  },
  {
    id: "testimonial-4",
    quote: "Setup was surprisingly easy. Within a week, our response time went from 4 hours to under 60 seconds. Game changer for our roofing business.",
    highlight: "under 60 seconds",
    author: "Roofing Company",
    role: "General Manager",
    company: "Fairbanks Area",
    industry: "Roofing & Exteriors",
    initials: "RC",
    gradient: "from-slate-400 to-zinc-500"
  }
];

// Social proof stats
const socialProofStats = [
  { id: "social-1", value: "2,500+", label: "Leads Captured" },
  { id: "social-2", value: "<60s", label: "Response Time" },
  { id: "social-3", value: "24/7", label: "Always On" },
  { id: "social-4", value: "97%", label: "Satisfaction Rate" }
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showLeadMagnet, setShowLeadMagnet] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  const openCalendly = () => {
    window.open("https://calendly.com/jondcurtis", "_blank");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 font-jakarta overflow-x-hidden relative">
      {/* Animated Aurora Background */}
      <AnimatedAuroraBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => scrollToSection("hero")} data-testid="nav-logo">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="font-outfit font-bold text-lg tracking-tight">Alaska AI Services</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection("problem")} className="text-slate-400 hover:text-white transition-colors" data-testid="nav-problem">The Problem</button>
              <button onClick={() => scrollToSection("solution")} className="text-slate-400 hover:text-white transition-colors" data-testid="nav-solution">Solution</button>
              <button onClick={() => scrollToSection("demo")} className="text-slate-400 hover:text-white transition-colors" data-testid="nav-demo">Demo</button>
              <button onClick={() => scrollToSection("pricing")} className="text-slate-400 hover:text-white transition-colors" data-testid="nav-pricing">Pricing</button>
              <button onClick={() => setShowLeadMagnet(true)} className="text-sky-400 hover:text-sky-300 transition-colors font-medium" data-testid="nav-free-guide">Free Guide</button>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Button 
                onClick={() => navigate('/client/login')}
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-slate-800/50 font-medium px-4 cursor-pointer"
                data-testid="nav-client-login"
              >
                Client Login
              </Button>
              <Button 
                onClick={() => navigate('/pricing')}
                variant="outline"
                className="border-sky-500/50 text-sky-400 hover:bg-sky-500/10 hover:text-sky-300 font-semibold px-5 cursor-pointer"
                data-testid="nav-get-started"
              >
                Get Started
              </Button>
              <Button 
                onClick={openCalendly}
                className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-semibold px-5 shadow-lg shadow-sky-500/25 cursor-pointer"
                data-testid="nav-cta"
              >
                Book Free Demo
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-slate-900 border-t border-slate-800"
          >
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection("problem")} className="block w-full text-left py-2 text-slate-300 hover:text-white" data-testid="mobile-nav-problem">The Problem</button>
              <button onClick={() => scrollToSection("solution")} className="block w-full text-left py-2 text-slate-300 hover:text-white" data-testid="mobile-nav-solution">Solution</button>
              <button onClick={() => scrollToSection("demo")} className="block w-full text-left py-2 text-slate-300 hover:text-white" data-testid="mobile-nav-demo">Demo</button>
              <button onClick={() => scrollToSection("pricing")} className="block w-full text-left py-2 text-slate-300 hover:text-white" data-testid="mobile-nav-pricing">Pricing</button>
              <button onClick={() => { setShowLeadMagnet(true); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-sky-400 hover:text-sky-300 font-medium" data-testid="mobile-nav-free-guide">Free Guide</button>
              <button onClick={() => { navigate('/client/login'); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-slate-300 hover:text-white" data-testid="mobile-nav-client-login">Client Login</button>
              <div className="flex flex-col gap-3 mt-4">
                <Button 
                  onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}
                  variant="outline"
                  className="w-full border-sky-500/50 text-sky-400 hover:bg-sky-500/10 font-semibold cursor-pointer"
                  data-testid="mobile-nav-get-started"
                >
                  Get Started
                </Button>
                <Button 
                  onClick={openCalendly}
                  className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold cursor-pointer"
                  data-testid="mobile-nav-cta"
                >
                  Book Free Demo
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content - Above Background */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section id="hero" className="relative min-h-screen flex items-center pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div 
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
                <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                <span className="text-sm text-slate-300">AI-Powered Lead Response</span>
              </motion.div>

              <motion.h1 
                variants={fadeInUp}
                className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-4"
              >
                Never Miss Another{" "}
                <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
                  Customer Call
                </span>
              </motion.h1>
              
              <motion.p 
                variants={fadeInUp}
                className="text-lg sm:text-xl text-sky-400 font-semibold mb-8"
              >
                AI-Powered Answering for Service Businesses
              </motion.p>

              <motion.p 
                variants={fadeInUp}
                className="text-xl sm:text-2xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed"
              >
                Your AI receptionist answers every call in under 60 seconds, qualifies leads, and books appointments — even when you're on a job site.
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  onClick={openCalendly}
                  size="lg"
                  className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold px-10 py-7 text-lg shadow-xl shadow-sky-500/30 cursor-pointer"
                  data-testid="hero-cta"
                >
                  Book a Free 10-Minute Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  onClick={() => navigate('/pricing')}
                  size="lg"
                  variant="outline"
                  className="border-sky-500/50 text-sky-400 hover:bg-sky-500/10 hover:text-sky-300 font-bold px-10 py-7 text-lg cursor-pointer"
                  data-testid="hero-get-started"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div 
                variants={fadeInUp}
                className="flex flex-wrap justify-center gap-8 mt-16 pt-8 border-t border-slate-800"
              >
                <div className="flex items-center gap-2 text-slate-400">
                  <Clock className="w-5 h-5 text-sky-400" />
                  <span>Responds in under 60 seconds</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <Calendar className="w-5 h-5 text-sky-400" />
                  <span>Auto-books appointments</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  <MessageSquare className="w-5 h-5 text-sky-400" />
                  <span>24/7 availability</span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section id="problem" className="py-24 sm:py-32 bg-slate-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20 mb-6">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400 font-medium">The Hard Truth</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Every Missed Call Costs You{" "}
                <span className="text-red-400">$1,500+</span>
              </h2>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
                You're on a job site. Your phone buzzes. You can't answer. That customer calls your competitor instead — and you just lost a $1,500 repair job you'll never know about.
              </p>
            </motion.div>

            {/* Problem Stats */}
            <motion.div 
              variants={fadeInUp}
              className="grid md:grid-cols-3 gap-6 mb-12"
            >
              {problemStats.map((item) => (
                <div 
                  key={item.id}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 text-center"
                  data-testid={`problem-stat-${item.id}`}
                >
                  <div className="font-outfit text-3xl sm:text-4xl font-bold text-red-400 mb-3">
                    {item.stat}
                  </div>
                  <p className="text-slate-400">{item.label}</p>
                </div>
              ))}
            </motion.div>

            {/* Additional Problem Point */}
            <motion.div 
              variants={fadeInUp}
              className="bg-slate-800/30 border border-slate-700/30 rounded-2xl p-8 max-w-2xl mx-auto text-center"
            >
              <Users className="w-10 h-10 text-slate-500 mx-auto mb-4" />
              <p className="text-lg text-slate-300">
                Answering services sound robotic and <span className="text-red-400 font-medium">can't book appointments</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solution" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 mb-6">
                <Zap className="w-4 h-4 text-sky-400" />
                <span className="text-sm text-sky-400 font-medium">The Solution</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                We Capture Every Lead —{" "}
                <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
                  Automatically
                </span>
              </h2>
            </motion.div>

            {/* How It Works */}
            <motion.div variants={fadeInUp} className="mb-20">
              <h3 className="font-outfit text-2xl font-bold text-center mb-10">How It Works</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {howItWorks.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: parseInt(item.step) * 0.1 }}
                    className="relative bg-slate-900/50 border border-slate-800 rounded-2xl p-6 hover:border-sky-500/50 transition-colors"
                    data-testid={`step-${item.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center font-outfit font-bold text-white mb-4">
                      {item.step}
                    </div>
                    <h4 className="font-outfit font-semibold text-lg mb-2">{item.title}</h4>
                    <p className="text-slate-400 text-sm">{item.description}</p>
                    
                    {parseInt(item.step) < howItWorks.length && (
                      <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                        <ArrowRight className="w-6 h-6 text-slate-700" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Benefits */}
            <motion.div variants={fadeInUp}>
              <h3 className="font-outfit text-2xl font-bold text-center mb-10">What You Get</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {benefits.map((benefit) => (
                  <div 
                    key={benefit.id}
                    className="flex items-center gap-3 bg-slate-900/30 rounded-xl px-5 py-4"
                    data-testid={`benefit-${benefit.id}`}
                  >
                    <CheckCircle2 className="w-5 h-5 text-sky-400 flex-shrink-0" />
                    <span className="text-slate-300">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What Sets Us Apart Section */}
      <section id="apart" className="py-24 sm:py-32 bg-gradient-to-b from-slate-900/50 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-400 font-medium">Our Competitive Edge</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                What Sets{" "}
                <span className="bg-gradient-to-r from-emerald-400 to-sky-500 bg-clip-text text-transparent">
                  Our System Apart
                </span>
              </h2>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
                We don't just answer calls — we've built something smarter.
              </p>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {/* Industry-Trained AI */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-8 hover:border-emerald-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-6">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-3">Industry-Trained Knowledge Base</h3>
                <p className="text-slate-400 leading-relaxed">
                  Our AI is pre-loaded with <span className="text-emerald-400 font-medium">industry-specific knowledge</span> — plumbing codes, HVAC troubleshooting, electrical regulations, and regional best practices. It doesn't just take messages; it understands your business.
                </p>
              </div>

              {/* Regional Intelligence */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-8 hover:border-sky-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-6">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-3">Regional Expertise Built-In</h3>
                <p className="text-slate-400 leading-relaxed">
                  Alaska-specific knowledge means your AI knows about <span className="text-sky-400 font-medium">frozen pipes, extreme cold protocols, and local codes</span>. Florida contractors get hurricane prep guidance. Every region has tailored intelligence.
                </p>
              </div>

              {/* Continuous Learning */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-8 hover:border-purple-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-6">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-3">Premium Contractors Contribute</h3>
                <p className="text-slate-400 leading-relaxed">
                  Our knowledge base <span className="text-purple-400 font-medium">grows with real contractor insights</span>. Premium subscribers can add their own FAQs and best practices, making the entire network smarter over time.
                </p>
              </div>

              {/* Instant Response */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-8 hover:border-amber-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-3">Under 60-Second Response</h3>
                <p className="text-slate-400 leading-relaxed">
                  While competitors put callers on hold, our AI <span className="text-amber-400 font-medium">responds instantly</span> — qualifying leads, answering questions, and booking appointments before your competition even picks up.
                </p>
              </div>

              {/* Full Automation */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-8 hover:border-rose-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center mb-6">
                  <Calendar className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-3">End-to-End Automation</h3>
                <p className="text-slate-400 leading-relaxed">
                  Most answering services just take messages. We <span className="text-rose-400 font-medium">book appointments directly into your calendar</span>, send confirmations, and remind customers automatically.
                </p>
              </div>

              {/* Multi-Channel */}
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-8 hover:border-cyan-500/30 transition-colors">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center mb-6">
                  <MessageSquare className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-3">Every Channel Covered</h3>
                <p className="text-slate-400 leading-relaxed">
                  Phone calls, SMS, WhatsApp — all handled by one AI that <span className="text-cyan-400 font-medium">remembers conversation context</span> across channels. Customers get seamless service however they reach out.
                </p>
              </div>
            </motion.div>

            {/* Bottom CTA */}
            <motion.div variants={fadeInUp} className="text-center mt-12">
              <p className="text-slate-400 mb-6">
                Ready to see the difference? Our AI is trained and ready for your industry.
              </p>
              <Button 
                onClick={openCalendly}
                className="bg-gradient-to-r from-emerald-500 to-sky-600 hover:from-emerald-600 hover:to-sky-700 text-white font-bold px-8 py-6 text-lg shadow-xl shadow-emerald-500/20 cursor-pointer"
                data-testid="apart-cta"
              >
                See It In Action
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section id="demo" className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-sm font-medium mb-6"
            >
              <Play className="w-4 h-4" />
              See It In Action
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
            >
              Watch How It <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-500">Works</span>
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-slate-400 text-lg max-w-2xl mx-auto"
            >
              See our AI receptionist handle a real customer call - from greeting to appointment booking in under 60 seconds.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Video Container - Supports YouTube or Custom Video URL */}
            <div className="relative aspect-video rounded-2xl overflow-hidden border border-slate-700/50 bg-slate-900 shadow-2xl shadow-sky-500/10">
              {/* 
                VIDEO CONFIGURATION:
                To add your demo video, replace the placeholder below with one of these options:
                
                Option 1 - YouTube Embed (Recommended):
                <iframe
                  src="https://www.youtube.com/embed/YOUR_VIDEO_ID?rel=0&modestbranding=1"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Alaska AI Services Demo"
                />
                
                Option 2 - Direct Video File:
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  controls
                  poster="/video-thumbnail.jpg"
                >
                  <source src="/demo-video.mp4" type="video/mp4" />
                </video>
                
                Option 3 - Loom Embed:
                <iframe
                  src="https://www.loom.com/embed/YOUR_LOOM_ID"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                />
              */}
              
              {/* Current Placeholder - Remove when adding video */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                <div className="w-20 h-20 rounded-full bg-sky-500/20 flex items-center justify-center mb-6 cursor-pointer hover:bg-sky-500/30 transition-colors group">
                  <Play className="w-8 h-8 text-sky-400 group-hover:text-sky-300 ml-1" />
                </div>
                <p className="text-slate-400 text-lg mb-2">Demo Video Coming Soon</p>
                <p className="text-slate-500 text-sm">Experience our AI receptionist in action</p>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -inset-4 bg-gradient-to-r from-sky-500/20 via-blue-500/20 to-purple-500/20 rounded-3xl blur-2xl -z-10 opacity-50" />
          </motion.div>

          {/* Video Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
          >
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-6 h-6 text-emerald-400" />
              </div>
              <h4 className="font-semibold mb-1">Natural Conversation</h4>
              <p className="text-slate-400 text-sm">AI understands context and responds naturally</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-xl bg-sky-500/10 flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-6 h-6 text-sky-400" />
              </div>
              <h4 className="font-semibold mb-1">Instant Booking</h4>
              <p className="text-slate-400 text-sm">Appointments scheduled in real-time</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mx-auto mb-3">
                <Bell className="w-6 h-6 text-purple-400" />
              </div>
              <h4 className="font-semibold mb-1">Instant Notifications</h4>
              <p className="text-slate-400 text-sm">You get alerted immediately via SMS & email</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section id="proof" className="py-24 sm:py-32 bg-slate-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 mb-6">
                <Users className="w-4 h-4 text-sky-400" />
                <span className="text-sm text-sky-400 font-medium">Trusted by Alaska Contractors</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold mb-4">
                Real Results from Real Businesses
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                See how Alaska contractors are capturing more leads and growing their businesses with AI automation.
              </p>
            </motion.div>

            {/* Stats Bar */}
            <motion.div 
              variants={fadeInUp}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
            >
              {socialProofStats.map((stat) => (
                <div 
                  key={stat.id}
                  className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-6 text-center"
                  data-testid={`social-stat-${stat.id}`}
                >
                  <div className="font-outfit text-3xl sm:text-4xl font-bold text-sky-400 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>

            {/* Testimonials Grid */}
            <motion.div 
              variants={fadeInUp}
              className="grid md:grid-cols-2 gap-6 mb-16"
            >
              {testimonials.map((testimonial) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: parseInt(testimonial.id.split('-')[1]) * 0.1 }}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 sm:p-8 relative hover:border-slate-600/50 transition-colors"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <div className="absolute -top-3 left-6 text-5xl text-sky-500/20 font-serif">"</div>
                  <blockquote className="text-lg text-slate-200 leading-relaxed mb-6 relative z-10">
                    {testimonial.quote.split(testimonial.highlight).map((part, i, arr) => (
                      <span key={`${testimonial.id}-part-${i}`}>
                        {part}
                        {i < arr.length - 1 && (
                          <span className="text-sky-400 font-semibold">{testimonial.highlight}</span>
                        )}
                      </span>
                    ))}
                  </blockquote>
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${testimonial.gradient} flex items-center justify-center font-semibold text-white text-sm`}>
                      {testimonial.initials}
                    </div>
                    <div>
                      <div className="font-semibold text-white">{testimonial.author}</div>
                      <div className="text-sm text-slate-500">{testimonial.role}, {testimonial.company}</div>
                      <div className="text-xs text-slate-600">{testimonial.industry}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Industries with State Selection */}
            <motion.div variants={fadeInUp}>
              <h3 className="font-outfit text-xl font-semibold text-center text-slate-400 mb-4">
                Industries We Serve
              </h3>
              <p className="text-center text-sm text-slate-500 mb-8">
                Select your industry, then choose your state for a localized AI demo
              </p>
              <div className="flex flex-wrap justify-center gap-4" ref={dropdownRef}>
                {industries.map((industry) => (
                  <div 
                    key={industry.slug}
                    className="relative"
                    data-testid={`industry-${industry.slug}`}
                  >
                    <button
                      onClick={() => setOpenDropdown(openDropdown === industry.slug ? null : industry.slug)}
                      className={`flex items-center gap-3 bg-slate-800/50 border rounded-full px-6 py-3 transition-all ${
                        openDropdown === industry.slug 
                          ? 'border-sky-500 bg-slate-800' 
                          : 'border-slate-700/50 hover:border-sky-500/50'
                      }`}
                      data-testid={`industry-btn-${industry.slug}`}
                    >
                      <industry.icon className="w-5 h-5 text-sky-400" />
                      <span className="text-slate-300">{industry.name}</span>
                      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${
                        openDropdown === industry.slug ? 'rotate-180' : ''
                      }`} />
                    </button>
                    
                    {/* State Selection Dropdown */}
                    <AnimatePresence>
                      {openDropdown === industry.slug && (
                        <motion.div
                          initial={{ opacity: 0, y: -10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -10, scale: 0.95 }}
                          transition={{ duration: 0.15 }}
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-xl shadow-xl shadow-black/30 overflow-hidden z-50"
                        >
                          <div className="p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <MapPin className="w-4 h-4 text-sky-400" />
                              <span className="text-sm font-medium text-white">Select Your State</span>
                            </div>
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  navigate(`/demo/${e.target.value}?industry=${industry.slug}`);
                                  setOpenDropdown(null);
                                }
                              }}
                              className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white cursor-pointer hover:border-sky-500 transition-colors text-sm"
                              data-testid={`state-select-${industry.slug}`}
                              defaultValue=""
                            >
                              <option value="" disabled>Choose a state...</option>
                              {statesList.map((state) => (
                                <option key={state.abbr} value={state.abbr}>{state.name}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="h-px bg-slate-700" />
                          
                          {/* Or explore the map */}
                          <button
                            onClick={() => {
                              navigate(`/us-demo?industry=${industry.slug}`);
                              setOpenDropdown(null);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700/50 transition-colors text-left"
                            data-testid={`map-demo-btn-${industry.slug}`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                              <MapPin className="w-4 h-4 text-emerald-400" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-white">Explore US Map</div>
                              <div className="text-xs text-slate-500">Click any state visually</div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-slate-500 ml-auto" />
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Featured Contractors Section */}
      <FeaturedContractors />

      {/* Who This Is For Section */}
      <section className="py-20 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold mb-4">
                Is This Right For{" "}
                <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
                  Your Business?
                </span>
              </h2>
            </motion.div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* This IS for you */}
              <motion.div variants={fadeInUp} className="bg-green-500/5 border border-green-500/20 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="font-outfit text-xl font-bold text-green-400">This IS For You If...</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    { id: "for-1", text: "You get 30+ inbound leads per month" },
                    { id: "for-2", text: "You're missing calls while on job sites" },
                    { id: "for-3", text: "Your follow-up takes hours (or days)" },
                    { id: "for-4", text: "You don't have dedicated office staff" },
                    { id: "for-5", text: "You're ready to book more estimates" },
                    { id: "for-6", text: "You want to grow without hiring" }
                  ].map((item) => (
                    <li key={item.id} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-300">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
              
              {/* This is NOT for you */}
              <motion.div variants={fadeInUp} className="bg-red-500/5 border border-red-500/20 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                    <X className="w-5 h-5 text-red-400" />
                  </div>
                  <h3 className="font-outfit text-xl font-bold text-red-400">This is NOT For You If...</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    { id: "not-1", text: "You have no inbound leads to follow up on" },
                    { id: "not-2", text: "You're not willing to answer when AI hands off hot leads" },
                    { id: "not-3", text: "You expect 100% automation with zero involvement" },
                    { id: "not-4", text: "You're not ready to handle more bookings" },
                    { id: "not-5", text: "You don't have a calendar or booking system" },
                    { id: "not-6", text: "You're looking for the cheapest option" }
                  ].map((item) => (
                    <li key={item.id} className="flex items-start gap-3">
                      <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-300">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-500/10 border border-sky-500/20 mb-6">
                <DollarSign className="w-4 h-4 text-sky-400" />
                <span className="text-sm text-sky-400 font-medium">Complete Digital Transformation</span>
              </div>
              <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
                Website{" "}
                <span className="bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
                  + AI Receptionist
                </span>
                {" "}in 48 Hours
              </h2>
              <p className="text-lg text-slate-400 mb-4">
                Professional website, AI phone answering, and lead capture — all in one package
              </p>
              <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
                We build your complete digital presence with AI-powered website generation and automated lead response. No designers needed, no long timelines.
              </p>
            </motion.div>

            {/* Pricing Cards Grid - 3 Tiers */}
            <motion.div variants={fadeInUp} className="grid lg:grid-cols-3 gap-6 mt-10">
              
              {/* Tier 1: Starter Digital Package */}
              <div className="bg-slate-900/80 border border-slate-700 rounded-3xl p-6 relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="font-outfit text-xl font-bold mb-1 text-white">Starter Digital</h3>
                  <p className="text-slate-400 text-sm mb-4">Perfect for new contractors</p>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="font-outfit text-4xl font-bold text-white">$4,997</span>
                    </div>
                    <div className="text-slate-400 text-sm">setup + $197/month</div>
                  </div>

                  {/* What's Included */}
                  <div className="text-left mb-6">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">Includes:</p>
                    <ul className="space-y-2">
                      {[
                        { id: "starter-1", text: "5-page AI website" },
                        { id: "starter-2", text: "AI phone receptionist" },
                        { id: "starter-3", text: "Lead capture dashboard" },
                        { id: "starter-4", text: "Basic analytics" },
                        { id: "starter-5", text: "Email support" }
                      ].map((item) => (
                        <li key={item.id} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300 text-xs">{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button 
                    onClick={openCalendly}
                    variant="outline"
                    size="lg"
                    className="w-full border-slate-600 text-white hover:bg-slate-800 text-sm"
                    data-testid="pricing-starter-cta"
                  >
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tier 2: Professional Package - RECOMMENDED */}
              <div className="bg-slate-900/80 border-2 border-sky-500/50 rounded-3xl p-6 relative overflow-hidden">
                {/* Recommended Badge */}
                <div className="absolute top-0 right-0 bg-sky-500 text-white text-xs font-bold px-3 py-1 rounded-bl-xl">
                  MOST POPULAR
                </div>
                
                {/* Glow effect */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[150px] bg-sky-500/10 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <h3 className="font-outfit text-xl font-bold mb-1 text-white">Professional</h3>
                  <p className="text-slate-400 text-sm mb-4">For growing contractors</p>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="font-outfit text-4xl font-bold text-white">$6,997</span>
                    </div>
                    <div className="text-slate-400 text-sm">setup + $397/month</div>
                  </div>

                  {/* What's Included */}
                  <div className="text-left mb-6">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">Everything in Starter, plus:</p>
                    <ul className="space-y-2">
                      {[
                        { id: "prof-1", text: "7-page custom website" },
                        { id: "prof-2", text: "AI phone + SMS automation" },
                        { id: "prof-3", text: "Advanced lead tracking" },
                        { id: "prof-4", text: "Calendar booking integration" },
                        { id: "prof-5", text: "Priority support" },
                        { id: "prof-6", text: "Weekly reports" }
                      ].map((item) => (
                        <li key={item.id} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300 text-xs">{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button 
                    onClick={openCalendly}
                    size="lg"
                    className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold shadow-xl shadow-sky-500/30 text-sm"
                    data-testid="pricing-professional-cta"
                  >
                    Schedule Free Demo
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tier 3: Enterprise Package */}
              <div className="bg-slate-900/80 border border-slate-700 rounded-3xl p-6 relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="font-outfit text-xl font-bold mb-1 text-white">Enterprise</h3>
                  <p className="text-slate-400 text-sm mb-4">For established contractors</p>
                  
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="font-outfit text-4xl font-bold text-white">$9,997</span>
                    </div>
                    <div className="text-slate-400 text-sm">setup + $597/month</div>
                  </div>

                  {/* What's Included */}
                  <div className="text-left mb-6">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-medium">Everything in Professional, plus:</p>
                    <ul className="space-y-2">
                      {[
                        { id: "ent-1", text: "Premium website + features" },
                        { id: "ent-2", text: "Full AI automation suite" },
                        { id: "ent-3", text: "CRM integration" },
                        { id: "ent-4", text: "Custom branding & design" },
                        { id: "ent-5", text: "Dedicated account manager" },
                        { id: "ent-6", text: "24/7 priority support" }
                      ].map((item) => (
                        <li key={item.id} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300 text-xs">{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button 
                    onClick={openCalendly}
                    variant="outline"
                    size="lg"
                    className="w-full border-slate-600 text-white hover:bg-slate-800 text-sm"
                    data-testid="pricing-enterprise-cta"
                  >
                    Contact Sales
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Guarantee - Reframed */}
            <motion.div variants={fadeInUp} className="mt-10">
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6 max-w-xl mx-auto">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Shield className="w-6 h-6 text-green-400" />
                  <span className="font-outfit font-semibold text-lg text-green-400">30-Day Performance Guarantee</span>
                </div>
                <p className="text-slate-400">
                  If we don't hit the agreed response and booking targets within 30 days, we'll fix it or refund you 100%. No questions asked.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold mb-6">
              Stop Losing Leads to Missed Calls
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              Book a free 10-minute demo and see how Alaska AI Services can capture every lead for your business.
            </p>
            <Button 
              onClick={openCalendly}
              size="lg"
              className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold px-10 py-6 text-lg shadow-xl shadow-sky-500/30 cursor-pointer"
              data-testid="final-cta"
            >
              Book a Free 10-Minute Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-800">

      {/* AI Chatbot */}
      <ChatBot />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <span className="font-outfit font-bold text-lg tracking-tight">Alaska AI Services</span>
              </div>
              <p className="text-slate-500 text-sm">
                AI-powered lead response and appointment booking for Alaska contractors.
              </p>
            </div>

            {/* Contact */}
            <div className="md:text-right">
              <div className="space-y-2">
                <a href="mailto:sales@alaskaaiservices.com" className="flex md:justify-end items-center gap-2 text-slate-400 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  sales@alaskaaiservices.com
                </a>
                <a href="mailto:info@alaskaaiservices.com" className="flex md:justify-end items-center gap-2 text-slate-400 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  info@alaskaaiservices.com
                </a>
                <a href="mailto:support@alaskaaiservices.com" className="flex md:justify-end items-center gap-2 text-slate-400 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                  support@alaskaaiservices.com
                </a>
                <div className="flex md:justify-end items-center gap-2 text-slate-400">
                  <MapPin className="w-4 h-4" />
                  Anchorage, Alaska
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col items-center gap-4">
            <div className="flex items-center gap-6 text-sm">
              <a href="/client/login" className="text-slate-500 hover:text-white transition-colors" data-testid="footer-client-login">Client Login</a>
              <a href="/blog" className="text-slate-500 hover:text-white transition-colors">Blog</a>
              <a href="/faq" className="text-slate-500 hover:text-white transition-colors">FAQ</a>
              <a href="/privacy" className="text-slate-500 hover:text-white transition-colors">Privacy</a>
              <a href="/terms" className="text-slate-500 hover:text-white transition-colors">Terms</a>
            </div>
            <p className="text-slate-600 text-sm">
              © 2026 Alaska AI Services. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      </div>{/* End Main Content Wrapper */}

      {/* Lead Magnet Popup */}
      <LeadMagnet forceOpen={showLeadMagnet} onClose={() => setShowLeadMagnet(false)} />
    </div>
  );
}
